/**
 * ModelDropdown Component
 *
 * Custom dropdown for displaying AI models with right-aligned capability icons
 */

import { useState, useRef, useEffect } from 'react';
import { Brain, Wrench, Image as ImageIcon, ChevronDown, Type } from 'lucide-react';

interface Model {
  id: string;
  name: string;
  contextWindow?: number;
  factoryCompatible?: boolean;
  reasoning?: boolean;
  tool_call?: boolean;
  has_vision?: boolean;
  has_text?: boolean;
}

interface ModelDropdownProps {
  models: Model[];
  value: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ModelDropdown({
  models,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a model'
}: ModelDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Find selected model
  const selectedModel = models.find((m) => m.id === value);

  // Separate factory-compatible from other models
  const factoryModels = models.filter((m) => m.factoryCompatible);
  const otherModels = models.filter((m) => !m.factoryCompatible);

  // Format context window (e.g., 128000 -> "128k")
  const formatContext = (context?: number) => {
    if (!context) return '';
    return `${Math.round(context / 1000)}k`;
  };

  // Render capability icons (with placeholders for alignment)
  const renderIcons = (model: Model) => (
    <div className="flex items-center gap-1 pl-2">
      {model.has_text ? (
        <Type className="w-3.5 h-3.5 text-gray-500" aria-label="Text input" />
      ) : (
        <div className="w-3.5 h-3.5" />
      )}
      {model.reasoning ? (
        <Brain className="w-3.5 h-3.5 text-purple-500" aria-label="Reasoning" />
      ) : (
        <div className="w-3.5 h-3.5" />
      )}
      {model.tool_call ? (
        <Wrench className="w-3.5 h-3.5 text-blue-500" aria-label="Tool calling" />
      ) : (
        <div className="w-3.5 h-3.5" />
      )}
      {model.has_vision ? (
        <ImageIcon className="w-3.5 h-3.5 text-green-500" aria-label="Vision" />
      ) : (
        <div className="w-3.5 h-3.5" />
      )}
      <span className="text-xs text-muted-foreground min-w-[32px] text-right">
        {model.contextWindow ? formatContext(model.contextWindow) : ''}
      </span>
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-left"
      >
        {selectedModel ? (
          <>
            <span className="truncate flex-1 min-w-0">
              {selectedModel.factoryCompatible && '🏭 '}
              {selectedModel.name} ({selectedModel.id})
            </span>
            {renderIcons(selectedModel)}
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        ) : (
          <>
            <span className="text-muted-foreground flex-1">{placeholder}</span>
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-input rounded-md shadow-lg max-h-80 overflow-auto">
          {/* Factory-compatible models */}
          {factoryModels.length > 0 && (
            <div>
              {factoryModels.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => {
                    onChange(model.id);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left hover:bg-secondary flex items-center ${
                    value === model.id ? 'bg-secondary' : ''
                  }`}
                >
                  <span className="truncate flex-1 min-w-0 text-sm">
                    🏭 {model.name} <span className="text-muted-foreground">({model.id})</span>
                  </span>
                  {renderIcons(model)}
                </button>
              ))}
              {otherModels.length > 0 && (
                <div className="border-t border-border my-1" />
              )}
            </div>
          )}

          {/* Other models */}
          {otherModels.length > 0 && (
            <div>
              {otherModels.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => {
                    onChange(model.id);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left hover:bg-secondary flex items-center ${
                    value === model.id ? 'bg-secondary' : ''
                  }`}
                >
                  <span className="truncate flex-1 min-w-0 text-sm">
                    {model.name} <span className="text-muted-foreground">({model.id})</span>
                  </span>
                  {renderIcons(model)}
                </button>
              ))}
            </div>
          )}

          {/* Empty state */}
          {models.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
              No models available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
